// frontend/src/hooks/useChartTheme.js
// The house style of every chart: app tokens, the user's palette, and the
// AG Charts theme built from both. components/charts/Chart.jsx applies it.

import { useMemo } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { isDarkTheme } from '../theme';
import useDataTheme from './useDataTheme';

// The ag-sheets and ag-material palettes of ag-charts-community 14.2.
export const CHART_PALETTES = {
  'ag-sheets': {
    fills: ['#5281d5', '#ff8d44', '#b5b5b5', '#ffd02f', '#6aabe6', '#7fbd57', '#8a8a8a', '#335287', '#717171', '#a98220'],
    strokes: ['#214d9b', '#c25600', '#7f7f7f', '#d59800', '#3575ac', '#4b861a', '#575757', '#062253', '#414141', '#734f00'],
  },
  'ag-sheets-dark': {
    fills: ['#4472C4', '#ED7D31', '#A5A5A5', '#FFC000', '#5B9BD5', '#70AD47', '#7B7B7B', '#264478', '#636363', '#997300'],
    strokes: ['#6899ee', '#ffa55d', '#cdcdcd', '#ffea53', '#82c3ff', '#96d56f', '#a1a1a1', '#47689f', '#878787', '#c0993d'],
  },
  'ag-material': {
    fills: ['#2196F3', '#FF9800', '#4CAF50', '#00BCD4', '#FFEB3B', '#7E57C2', '#9E9E9E', '#F06292', '#795548', '#F44336'],
    strokes: ['#1565C0', '#E65100', '#2E7D32', '#00838F', '#F9A825', '#4527A0', '#616161', '#C2185B', '#4E342E', '#B71C1C'],
  },
  'ag-material-dark': {
    fills: ['#2196F3', '#FF9800', '#4CAF50', '#00BCD4', '#FFEB3B', '#7E57C2', '#9E9E9E', '#F06292', '#795548', '#F44336'],
    strokes: ['#90CAF9', '#FFCC80', '#A5D6A7', '#80DEEA', '#FFF9C4', '#B39DDB', '#E0E0E0', '#F48FB1', '#A1887F', '#EF9A9A'],
  },
};

const FONT_SIZE = 11;

export const CHART_DEFAULTS = {
  padding: { top: 10, right: 16, bottom: 10, left: 16 },
  legend: {
    spacing: 16,
    item: {
      marker: { shape: 'circle', size: 8 },
      padding: { top: 6, right: 12, bottom: 6, left: 12 },
    },
  },
};

function getCssVar(name) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

function readTokens() {
  return {
    textColor: getCssVar('--color-text-primary'),
    textSecondary: getCssVar('--color-text-secondary'),
    mutedColor: getCssVar('--color-text-tertiary'),
    gridColor: getCssVar('--color-border'),
    fillColor: getCssVar('--color-fill'),
    primaryColor: getCssVar('--color-primary'),
    warningColor: getCssVar('--color-warning'),
    successColor: getCssVar('--color-success'),
    criticalColor: getCssVar('--color-error'),
    surfaceColor: getCssVar('--color-surface'),
    tooltipColor: getCssVar('--tooltip-background'),
    tooltipTextColor: getCssVar('--tooltip-text'),
    tooltipSubtleTextColor: getCssVar('--tooltip-subtle-text'),
    fontFamily: getCssVar('--font-family-primary'),
  };
}

function themeParams(tokens) {
  return {
    backgroundColor: tokens.surfaceColor,
    chromeBackgroundColor: tokens.surfaceColor,
    foregroundColor: tokens.textColor,
    subtleTextColor: tokens.mutedColor,
    chromeSubtleTextColor: tokens.mutedColor,
    borderColor: tokens.gridColor,
    axisLineColor: tokens.gridColor,
    gridLineColor: tokens.gridColor,
    accentColor: tokens.primaryColor,
    tooltipBackgroundColor: tokens.tooltipColor,
    tooltipTextColor: tokens.tooltipTextColor,
    tooltipSubtleTextColor: tokens.tooltipSubtleTextColor,
    fontFamily: tokens.fontFamily,
  };
}

function themeOverrides(tokens) {
  const categoryAxis = {
    label: { fontSize: FONT_SIZE },
    gridLine: { enabled: false },
    crosshair: {
      enabled: true,
      snap: true,
      stroke: tokens.mutedColor,
      strokeWidth: 1,
      lineDash: [4, 4],
      label: { enabled: false },
    },
    crossLines: {
      label: {
        fontSize: FONT_SIZE,
        color: tokens.textSecondary,
        fill: tokens.surfaceColor,
        fillOpacity: 0.9,
        padding: 4,
        cornerRadius: 4,
      },
    },
  };
  const lineSeries = {
    strokeWidth: 2,
    interpolation: { type: 'smooth' },
    highlight: { enabled: false },
    marker: { enabled: false },
  };
  return {
    common: {
      animation: { duration: 600 },
      background: { fill: 'transparent' },
      contextMenu: { enabled: false },
      padding: CHART_DEFAULTS.padding,
      title: { fontSize: 16, fontWeight: 600 },
      subtitle: { fontSize: 13 },
      legend: {
        position: 'bottom',
        spacing: CHART_DEFAULTS.legend.spacing,
        item: { ...CHART_DEFAULTS.legend.item, label: { fontSize: FONT_SIZE } },
      },
      axes: {
        number: {
          label: { fontSize: FONT_SIZE },
          title: { fontSize: 12, fontWeight: 400 },
          line: { enabled: false },
          crosshair: { enabled: false },
          gridLine: { style: [{ strokeWidth: 1 }] },
        },
        category: categoryAxis,
        'ordinal-time': categoryAxis,
      },
    },
    bar: { series: { strokeWidth: 0, cornerRadius: 4, highlight: { enabled: false } } },
    line: { series: lineSeries },
    area: { series: lineSeries },
  };
}

/** The AG base theme of a chart theme under the app's data-theme. */
export function chartBaseTheme(chartTheme, dataTheme) {
  const baseTheme = isDarkTheme(dataTheme) ? `${chartTheme}-dark` : chartTheme;
  if (!CHART_PALETTES[baseTheme]) throw new Error(`useChartTheme: no palette for chart theme ${baseTheme}`);
  return baseTheme;
}

export function useChartTheme() {
  const { user } = useAuth();
  const dataTheme = useDataTheme();

  return useMemo(() => {
    const tokens = readTokens();
    const baseTheme = chartBaseTheme(user.chart_theme, dataTheme);
    const palette = CHART_PALETTES[baseTheme];
    return {
      ...tokens,
      palette,
      agTheme: { baseTheme, palette, params: themeParams(tokens), overrides: themeOverrides(tokens) },
    };
  }, [user.chart_theme, dataTheme]);
}
